// Utilidades de calendario para la entrevista — un solo lugar con la lógica de
// fecha/hora (evita duplicar el cómputo entre Google Calendar y el .ics), y
// preparado para sumar más proveedores (Outlook, etc.) sin rediseñar la card:
// solo hay que agregar un builder acá + una opción en CalendarPickerSheet.
const pad2 = (n) => String(n).padStart(2, "0");
const floatingStamp = (y, mo, d, hh, mm) => `${y}${pad2(mo)}${pad2(d)}T${pad2(hh)}${pad2(mm)}00`;

// InterviewDetails no guarda una zona horaria explícita, así que la hora se
// trata como "hora local flotante" (sin sufijo "Z"/UTC): lo que la empresa
// escribió como 13:00 debe llegar al calendario como 13:00 literal, nunca
// convertida/desplazada por el dispositivo del candidato (por eso nunca se usa
// Date.toISOString() para esto — reinterpretaría los campos como hora local
// del dispositivo y los correría a UTC).
function computeInterviewWindow(iv) {
  const [y, mo, d] = (iv.date || "").split("-").map(Number);
  if (!y || !mo || !d) return null;
  const [hhRaw, mmRaw] = (iv.time || "09:00").split(":").map(Number);
  const startH = Number.isFinite(hhRaw) ? hhRaw : 9;
  const startM = Number.isFinite(mmRaw) ? mmRaw : 0;

  // end_time real si existe; si no, 60 min por defecto SOLO para armar el
  // evento (nunca se persiste esta duración por defecto en el backend).
  let endH, endM, endDateObj;
  const [endHRaw, endMRaw] = (iv.end_time || "").split(":").map(Number);
  if (iv.end_time && Number.isFinite(endHRaw) && Number.isFinite(endMRaw)) {
    endH = endHRaw; endM = endMRaw; endDateObj = new Date(y, mo - 1, d);
  } else {
    const endTotalMin = startH * 60 + startM + 60;
    endH = Math.floor(endTotalMin / 60) % 24;
    endM = endTotalMin % 60;
    endDateObj = new Date(y, mo - 1, d + Math.floor(endTotalMin / (24 * 60))); // solo aritmética de calendario
  }
  return {
    startStamp: floatingStamp(y, mo, d, startH, startM),
    endStamp: floatingStamp(endDateObj.getFullYear(), endDateObj.getMonth() + 1, endDateObj.getDate(), endH, endM),
  };
}

function interviewSummary(app, t) {
  return [t("myJobs.interviewLabel"), app.job_title, app.company_name].filter(Boolean).join(" — ");
}

function interviewDetailLines(app, iv, t) {
  const modalityLabel = iv.modality ? t(`notifyApplicant.modality_${iv.modality}`) : null;
  return [
    app.job_title ? `${t("myJobs.calendarPositionLabel")}: ${app.job_title}` : null,
    app.company_name ? `${t("myJobs.calendarCompanyLabel")}: ${app.company_name}` : null,
    iv.contact_name ? `${t("myJobs.withLabel")}: ${iv.contact_name}` : null,
    iv.contact_role ? `${t("myJobs.calendarContactRoleLabel")}: ${iv.contact_role}` : null,
    modalityLabel ? `${t("myJobs.calendarModalityLabel")}: ${modalityLabel}` : null,
    iv.modality === "video" && iv.link ? `${t("myJobs.calendarLinkLabel")}: ${iv.link}` : null,
    iv.notes ? `${t("myJobs.calendarNotesLabel")}: ${iv.notes}` : null,
    t("myJobs.calendarFooter"),
  ].filter(Boolean);
}

// Google Calendar "Crear evento" vía URL TEMPLATE (sin OAuth, sin Calendar API).
export function buildGoogleCalendarUrl(app, t) {
  const iv = app.interview_details || {};
  const win = computeInterviewWindow(iv);
  if (!win) return null;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: interviewSummary(app, t),
    dates: `${win.startStamp}/${win.endStamp}`,
    details: interviewDetailLines(app, iv, t).join("\n"),
  });
  // Nunca usar el link de videollamada como location — solo direcciones reales.
  if (iv.modality === "in_person" && iv.location) params.set("location", iv.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function icsEscape(str) {
  return String(str).replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
}

// Evento .ics estándar (RFC 5545) — compatible con Apple Calendar (iPhone/iPad/
// Mac) y cualquier cliente iCalendar. UID estable por aplicación: si se
// reimporta tras una reprogramación, el cliente de calendario puede actualizar
// el mismo evento en vez de duplicarlo.
export function buildInterviewICS(app, t) {
  const iv = app.interview_details || {};
  const win = computeInterviewWindow(iv);
  if (!win) return null;
  const dtStamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//InfoCityJobs//Interview//ES",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:interview-${app.application_id}@infocityjobs.com`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART:${win.startStamp}`,
    `DTEND:${win.endStamp}`,
    `SUMMARY:${icsEscape(interviewSummary(app, t))}`,
    `DESCRIPTION:${icsEscape(interviewDetailLines(app, iv, t).join("\n"))}`,
  ];
  // Nunca usar el link de videollamada como LOCATION — solo direcciones reales.
  if (iv.modality === "in_person" && iv.location) lines.push(`LOCATION:${icsEscape(iv.location)}`);
  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.join("\r\n");
}

export function downloadICS(icsContent, filename = "entrevista.ics") {
  const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

export const isIOSDevice = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

// iOS en modo standalone (PWA instalada) atrapa la navegación dentro de su
// propio WKWebView — Google detecta ese webview embebido y en vez de mostrar
// "Crear evento", redirige a la landing comercial de workspace.google.com.
// `x-safari-` es el prefijo documentado de iOS para forzar que el link se
// abra en Safari real (fuera del contenedor de la PWA).
export function openGoogleCalendarUrl(url, isStandaloneIOS) {
  if (isStandaloneIOS) {
    window.location.href = url.replace(/^https:\/\//, "x-safari-https://");
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}
